import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog } from 'antd-mobile';
import { useSafeState } from 'ahooks';
import { useDispatch } from 'react-redux';
import lookie from 'lookie';
import { useNavigate } from 'react-router';
import { Icon } from '@fuxi/eevee-icon';
import { CheckCircleFilled, Loading3QuartersOutlined } from '@ant-design/icons';
import { App } from '@capacitor/app';
import noMachinImg from '@/assets/image/no-machine.png';
import { isShowMachineLockState } from '@/pages/device/device-management';
import { setCurrentServingOrder } from '@/store/service';
import notEnoughTimeBg from '@/assets/image/not-enough-time-bg.png';
import service from '@/service';
import { handleNavFromOrderInfo, transformVipName2Text, Toast, showBirthday } from '@/utils';
import { BODY_PART, DeviceStatePhoto, MachineState, SELECTED_CUSTOMER_ID, UPPER_STATUS_CODE } from '@/constant';
import { memberVip } from '@/pages/home';
import { useScanQRCode } from '@/hooks/useScanQRCode';
import { CustomToast, CustomToastRefAttrs } from '../toast';
import ScrollView, { ScrollViewRefAttrs } from '../scroll-view';

import cx from './index.module.scss';

interface Props {
  orderInfo: GetOrderFromWxResponse;
  isConfirmedPlan: boolean;
  setIsConfirmedPlan: Dispatch<SetStateAction<boolean>>;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onVerifyWxOrder: VoidFunc;
  onOrderEndOfTimeout: VoidFunc;
}

const OrderFromWechat: React.FC<Props> = ({
  isConfirmedPlan,
  setIsConfirmedPlan,
  orderInfo,
  setIsDrawerOpen,
  onVerifyWxOrder,
  onOrderEndOfTimeout,
}) => {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [deviceList, setDeviceList] = useSafeState<DeviceItem[]>([]);
  const [selectDeviceId, setSelectDeviceId] = useSafeState(null);
  const [isLoadingDevice, setIsLoadingDevice] = useSafeState(true);
  const [isConfirmingPlan, setIsConfirmingPlan] = useState(false);
  const [isStartingServing, setIsStartingServing] = useState(false);
  const [isConfirmNotEnoughTime, setIsConfirmNotEnoughTime] = useState(false);
  const verifyWxOrderId = useRef(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const customToastRef = useRef<CustomToastRefAttrs>(null);

  const { scanQR } = useScanQRCode({ tips: '扫描耗材二维码' });

  const getDevices = useCallback(async () => {
    try {
      const { data } = await service.device.getDeviceList();

      const allStateMachine: Record<DeviceItem['state'], any> = {} as any;
      data
        ?.filter(item => !isShowMachineLockState(item))
        .forEach(item => {
          allStateMachine[item.state] = allStateMachine[item.state] ? [...allStateMachine[item.state], item] : [item];
        });

      const lockedMachineList = data?.filter(item => isShowMachineLockState(item));

      const sortedMachine: DeviceItem[] = [
        ...(allStateMachine['Idle'] || []),
        ...(allStateMachine['ManualOnly'] || []),
        ...(allStateMachine['Busy'] || []),
        ...(allStateMachine['Abnormal'] || []),
        ...(lockedMachineList || []),
        ...(allStateMachine['Offline'] || []),
      ];

      setDeviceList(sortedMachine);
    } finally {
      scrollViewRef.current?.handleRefresh();
      setIsLoadingDevice(false);
    }
  }, [setDeviceList, setIsLoadingDevice]);

  const handleScanQrConfirmPlan = useCallback(async () => {
    try {
      const qrRes = await scanQR();
      const { data } = await service.service.verifyWxOrder({
        consumableUniqueCode: qrRes?.[0],
        payOrderId: orderInfo?.payOrderId,
        deviceId: selectDeviceId,
      });

      onVerifyWxOrder?.();
      setIsConfirmedPlan(true);
      verifyWxOrderId.current = data?.servieOrderId;
    } catch (e) {
      setTimeout(() => Toast.show(e?.message), 0);
    }
  }, [onVerifyWxOrder, orderInfo?.payOrderId, scanQR, selectDeviceId, setIsConfirmedPlan]);

  const handleConfirmPlan = useCallback(async () => {
    try {
      if (!selectDeviceId) {
        customToastRef.current?.fail('请选择一台理疗设备');
        return;
      }

      setIsConfirmingPlan(true);
      const { data } = await service.service.verifyWxOrder({
        payOrderId: orderInfo?.payOrderId,
        deviceId: selectDeviceId,
      });
      onVerifyWxOrder?.();

      setIsConfirmedPlan(true);
      verifyWxOrderId.current = data?.servieOrderId;
    } catch (e) {
      if (e?.code === UPPER_STATUS_CODE.ChassisLockingOrUnLocking) {
        setTimeout(() => Toast.show('机器正在解锁或停驻中'), 0);
      }
      // 这里先进行下单，如果有需要消耗品二维码的话，在进行下单进行二维码扫描，再进行下单
      if (e?.code === UPPER_STATUS_CODE.ConsumablesQRNotUploadError) {
        handleScanQrConfirmPlan();
      }
      if (
        e?.code === UPPER_STATUS_CODE.DEVICE_SOFTWARE_BY_PRODUCT_MIN_VERSION ||
        e?.code === UPPER_STATUS_CODE.DEVICE_SOFTWARE_BY_PRODUCT_MAX_VERSION
      ) {
        // 机器人版本不支持某方案
        Dialog.alert({
          title: (
            <div>
              <div>当前机器人版本不支持</div>
              <div style={{ marginTop: '3px' }}>{'「' + orderInfo?.goodsName + '」方案'}</div>
            </div>
          ),
          content: (
            <div>
              <div>请请联系交付工程师</div>
              <div style={{ marginTop: '3px' }}>
                更新至<span style={{ color: '#0055FF' }}>{e?.message}</span>版本使用
              </div>
            </div>
          ),
          confirmText: '我知道了',
          bodyClassName: cx('software-not-support'),
          onConfirm() {},
        });
      }
    } finally {
      setIsConfirmingPlan(false);
    }
  }, [handleScanQrConfirmPlan, onVerifyWxOrder, orderInfo, selectDeviceId, setIsConfirmedPlan]);

  const startServing = useCallback(async () => {
    try {
      setIsStartingServing(true);
      await service.service.startServing({
        serviceOrderId: verifyWxOrderId.current || orderInfo?.serviceOrderId,
      });
      const { data: newServingOrder } = await service.service.getServingOrderByOperator();
      lookie.set(SELECTED_CUSTOMER_ID, newServingOrder?.customerId);
      dispatch(setCurrentServingOrder(newServingOrder));
      handleNavFromOrderInfo(newServingOrder, nav);
    } catch (e) {
      if (e?.code === UPPER_STATUS_CODE.ORDER_END_OF_TIMEOUT) {
        Toast.show('检测到长时间未操作，已为您自动结束订单');
        setIsDrawerOpen(false);
        onOrderEndOfTimeout?.();
      } else if (e?.code === UPPER_STATUS_CODE.DEVICE_SOFTWARE_VERSION_TOO_LOW) {
        Dialog.alert({
          title: '检测到更新',
          content: '我们对现有产品进行了修复和优化，请尽快完成更新。',
          confirmText: '我知道了',
          onConfirm() {
            App.exitApp().catch(err => {
              if (err.message === 'Not implemented on web.') {
                window?.location?.reload();
              }
            });
          },
        });
      }
      // 机器人软件版本过低的情况
      else if (e?.code === UPPER_STATUS_CODE.OP_DEVICE_SOFTWARE_TOO_LOW) {
        Dialog.alert({
          title: '机器人升级提醒',
          content: (
            <div>
              <span>软件版本过低，需升级后才可正常运行，</span>
              <br />
              <span>请联系工作人员进行处理</span>
            </div>
          ),
          confirmText: '我知道了',
          onConfirm() {},
        });
      }
    } finally {
      setIsStartingServing(false);
    }
  }, [dispatch, nav, onOrderEndOfTimeout, orderInfo?.serviceOrderId, setIsDrawerOpen]);

  useEffect(() => {
    getDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('order-from-wechat')}>
      <CustomToast ref={customToastRef} />
      {orderInfo?.capacityEnough === false && !isConfirmNotEnoughTime ? (
        <div className={cx('time-not-enough-box')}>
          <div className={cx('content-wrapper')}>
            <div className={cx('img-box')}>
              <img src={notEnoughTimeBg} alt="" />
            </div>
            <div className={cx('tips-text')}>
              门店在 <span className={cx('highlight')}>{orderInfo?.capacityNotEnoughTime}</span>{' '}
              有预约单，本次服务时间可能不足
            </div>
          </div>
          <div className={cx('btn-wrapper')}>
            <Button className={cx('btn')} block onClick={() => setIsDrawerOpen(false)}>
              取消
            </Button>
            <Button className={cx('btn', 'btn-primary')} block onClick={() => setIsConfirmNotEnoughTime(true)}>
              继续服务
            </Button>
          </div>
        </div>
      ) : isConfirmedPlan ? (
        <div className={cx('start-service')}>
          <div className={cx('pic-wrapper')} style={{ backgroundImage: `url(${orderInfo?.bodyPreviewImage})` }}>
            <div className={cx('service-info')}>
              <div className={cx('header')}>
                <div className={cx('left')}>{orderInfo?.goodsName}</div>
                <div className={cx('right')}>
                  <div className={cx('time')}>{orderInfo?.minutes?.toString()?.padStart(2, '0')}:00</div>
                  <div>服务时长</div>
                </div>
              </div>
              <div className={cx('content')}>{orderInfo?.goodsIntro}</div>
            </div>
          </div>
          <div className={cx('btn-wrapper')}>
            <Button loading={isStartingServing} className={cx('btn')} block onClick={startServing}>
              开始服务
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={cx('user-info')}>
            <div className={cx('avatar')}>
              <img src={orderInfo?.avatarUrl} alt="avatar" />
              <img src={memberVip} alt="vip" className={cx('member-vip')} />
            </div>
            <div className={cx('info-text')}>
              <div className={cx('user-info-text')}>
                <div>{orderInfo?.userName}</div>
                <div className={cx('line')}></div>
                <div>{showBirthday(orderInfo?.age, orderInfo?.birthday)}</div>
              </div>

              {!!orderInfo.userVipName && (
                <div className={cx('card-info', transformVipName2Text(orderInfo.userVipName))}>
                  {orderInfo.userVipName}
                </div>
              )}
            </div>
          </div>
          <div className={cx('plan-info')}>
            <div className={cx('pic')} style={{ backgroundImage: `url(${orderInfo.wxCoverImage})` }}></div>
            <div className={cx('detail')}>
              <div className={cx('plan-name')}>{orderInfo.goodsName}</div>
              <div className={cx('tags')}>
                <span className={cx('tag-massage-type')}>
                  {(BODY_PART[orderInfo.bodyPart] ? `${BODY_PART[orderInfo.bodyPart]}·` : '') + '按摩'}
                </span>
                <span className={cx('tag-item')}>{orderInfo.minutes + '分钟'}</span>
              </div>
              <div className={cx('reserve-time')}>
                <span>{'预定时间：' + orderInfo.time}</span>
              </div>
            </div>
          </div>
          <div className={cx('machine-list')}>
            <div className={cx('header')}>选择机器</div>
            <ScrollView ref={scrollViewRef} scrollX={true} scrollY={false} className={cx('list-wrapper')}>
              <div className={cx('scroll-box')}>
                {isLoadingDevice ? (
                  <div className={cx('loading-wrapper')}>
                    <Loading3QuartersOutlined spin className={cx('icon')} />
                  </div>
                ) : (
                  <>
                    {deviceList?.length ? (
                      deviceList?.map(item => (
                        <div
                          onClick={() =>
                            item.state === 'Idle' && item?.controlState !== 'lock' && setSelectDeviceId(item.deviceId)
                          }
                          key={item.deviceId}
                          className={cx('device-item')}
                        >
                          <div
                            className={cx['device-image']}
                            style={{
                              backgroundImage: `url(${DeviceStatePhoto[item.state === 'Busy' ? 'online' : 'offline']})`,
                            }}
                          />
                          <div className={cx('device-card', { selected: selectDeviceId === item.deviceId })}>
                            <div className={cx('device-info')}>
                              <div className={cx('device-name')}>{item.deviceName}</div>
                              <div className={cx('device-id')}>{item.deviceId}</div>
                              <div
                                className={cx(
                                  'status',
                                  isShowMachineLockState(item) ? 'idle' : item.state?.toLowerCase()
                                )}
                              >
                                {isShowMachineLockState(item) ? '已锁机' : MachineState[item.state]}
                              </div>
                              {!!item.errorMessage && !isShowMachineLockState(item) && (
                                <div className={cx('recover-tips')}>
                                  <Icon className={cx('icon')} name="info-circle" />
                                  <span>{item.errorMessage}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {selectDeviceId === item.deviceId && (
                            <div className={cx('selected-device')}>
                              <div className={cx('selected-device-text')}>已选择</div>
                              <CheckCircleFilled className={cx('selected-device-icon')} />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className={cx('no-machine')}>
                        <img src={noMachinImg} alt="no-machine" />
                        <div className={cx('no-machine-text')}>暂无可用理疗设备</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </ScrollView>
          </div>
          <div className={cx('footer')}>
            <div className={cx('tips-text')}>
              以上信息需要给用户仔细核对，请实际确认机器人处于空闲状态时再点击“确认方案”
            </div>
            <Button loading={isConfirmingPlan} className={cx('btn')} block onClick={handleConfirmPlan}>
              确认方案
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderFromWechat;
